import { useRef } from 'react';

/**
 * CORE-1919 — адрес файла записи Live теперь выдаёт API со сроком:
 * `https://api…/api/media/rec/{exp}/{sig}/{store}/{dir}/{file}`.
 *
 * Срок округлён бэкендом до окна (6 ч): в пределах окна адрес одной записи не
 * меняется, но на границе окна каждый перезапрос эфира/комнаты (экраны опрашивают
 * их раз в 10–30 с) приносит новый адрес той же записи — и плеер, зависящий от
 * `url`, начинал бы воспроизведение заново. Здесь — ключ «тот же файл» без срока
 * и подписи и хук, который держит прежний адрес, пока тот не близок к истечению.
 */

const REC_PATH = /\/api\/media\/rec\/(\d+)\/[^/]+\/([^/]+)\/([^/]+)\/(.+)$/;

/** Сколько ещё должен жить прежний адрес, чтобы не менять его на новый. */
export const KEEP_IF_VALID_FOR_MS = 60 * 60_000;

function decodeBase64Url(value: string): string | null {
  try {
    const b64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    if (typeof atob !== 'function') return null;
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return decodeURIComponent(Array.from(bytes, (b) => '%' + b.toString(16).padStart(2, '0')).join(''));
  } catch {
    return null;
  }
}

function pathOf(url: string): string {
  return url.replace(/^[a-z]+:\/\/[^/]+/i, '').split(/[?#]/)[0];
}

/** Срок адреса записи (мс эпохи) или null, если это не адрес со сроком. */
export function recordingUrlExpiresAt(url: string | null | undefined): number | null {
  if (!url) return null;
  const m = pathOf(url).match(REC_PATH);
  return m ? Number(m[1]) * 1000 : null;
}

/**
 * Ключ файла: для адреса API со сроком — `/{dir}/{file}` объекта в хранилище
 * (тот же путь, что у прежней прямой ссылки бакета AWS), иначе — путь адреса
 * без хоста, query и `#`.
 */
export function recordingMediaKey(url: string | null | undefined): string | null {
  if (!url) return null;
  const path = pathOf(url);
  const m = path.match(REC_PATH);
  if (m) {
    const dir = decodeBase64Url(m[3]);
    return dir ? `/${dir}/${decodeURIComponent(m[4])}` : `/api/media/rec/${m[2]}/${m[3]}/${m[4]}`;
  }
  return path || null;
}

/**
 * Решение для хука, отдельно — для тестов: оставить прежний адрес записи, если это
 * тот же файл и прежний проживёт ещё хотя бы `KEEP_IF_VALID_FOR_MS`.
 */
export function pickStableMediaUrl(
  previous: string | null | undefined,
  next: string | null | undefined,
  nowMs: number,
): string | null | undefined {
  if (!previous || !next || previous === next) return next;
  if (recordingMediaKey(previous) !== recordingMediaKey(next)) return next;
  // Срок известен только у адреса записи; прочие адреса — как раньше.
  const exp = recordingUrlExpiresAt(previous);
  if (exp === null || exp - nowMs < KEEP_IF_VALID_FOR_MS) return next;
  return previous;
}

/** Адрес для плеера: не меняется, пока API отдаёт тот же файл с новым сроком. */
export function useStableMediaUrl<T extends string | null | undefined>(url: T): T {
  const ref = useRef<T>(url);
  const picked = pickStableMediaUrl(ref.current, url, Date.now()) as T;
  ref.current = picked;
  return picked;
}
